/**
 * Landing page constants. Update press article URLs here when available.
 */
export const PRESS_LINKS = {
    diarioFinanciero:
        'https://www.df.cl/capital/cultura/fabrizio-copano-y-humberto-sichel-crean-un-nuevo-medio-fabuloso',
    audioGen:
        'https://audiogen.substack.com/i/175692255/3-impacto-publicitario-del-podcast-en-chile',
    entreNosotras:
        'https://chile.as.com/tikitakas/fenomeno-digital-podcast-de-las-hermanas-araneda-se-acerca-al-millon-y-sube-en-rankings-f202605-n/',
    theClinic:
        'https://www.theclinic.cl/2026/04/09/julio-cesar-rodriguez-y-su-futuro-como-conductor-y-ejecutivo-en-la-tv-chilena-estoy-a-un-paso-de-llegar-a-un-medio-importante-de-chile/',
    radioAgricultura:
        'https://www.radioagricultura.cl/entretencion/a-que-canal-llega-julio-cesar-rodriguez-adelanta-su-proximo-gran-paso-en-tv_20260409/',
    instagram: 'https://www.instagram.com/p/Db6D4aska-e/?hl=es&img_index=1',
} as const;

export type PressLinkKey = keyof typeof PRESS_LINKS;

export const PRESS_LOGOS: Record<PressLinkKey, string> = {
    diarioFinanciero: '/images/press/diario-financiero.png',
    audioGen: '/images/press/audiogen.png',
    entreNosotras: '/images/press/as-chile.png',
    theClinic: '/images/press/the-clinic.png',
    radioAgricultura: '/images/press/radio-agricultura.png',
    /** Instagram account avatar (main image); platform badge is separate */
    instagram: '/images/press/daniel-m-t.png',
};

export const INSTAGRAM_PLATFORM_BADGE = '/images/press/instagram.svg';

export const PRESS_MENTIONS = [
    {
        key: 'diarioFinanciero' as const,
        label: 'Diario Financiero',
        href: PRESS_LINKS.diarioFinanciero,
        logo: PRESS_LOGOS.diarioFinanciero,
    },
    {
        key: 'audioGen' as const,
        label: 'AudioGen.fm',
        href: PRESS_LINKS.audioGen,
        logo: PRESS_LOGOS.audioGen,
    },
    {
        key: 'entreNosotras' as const,
        label: 'AS Chile',
        href: PRESS_LINKS.entreNosotras,
        logo: PRESS_LOGOS.entreNosotras,
    },
    {
        key: 'theClinic' as const,
        label: 'The Clinic',
        href: PRESS_LINKS.theClinic,
        logo: PRESS_LOGOS.theClinic,
    },
    {
        key: 'radioAgricultura' as const,
        label: 'Radio Agricultura',
        href: PRESS_LINKS.radioAgricultura,
        logo: PRESS_LOGOS.radioAgricultura,
    },
    {
        key: 'instagram' as const,
        label: 'Instagram · @daniel_m_t',
        href: PRESS_LINKS.instagram,
        logo: PRESS_LOGOS.instagram,
        badgeLogo: INSTAGRAM_PLATFORM_BADGE,
    },
    {
        key: 'instagramFabuloso' as const,
        label: 'Instagram · @somosfabuloso',
        href: 'https://www.instagram.com/p/DagU8UmD5hx/?hl=es&img_index=6',
        logo: '/images/press/somos-fabuloso.jpg',
        badgeLogo: INSTAGRAM_PLATFORM_BADGE,
    },
    {
        key: 'instagramCorazonDelNegocio' as const,
        label: 'Instagram · @corazondelnegocio',
        href: 'https://www.instagram.com/p/DaV9LweAKiA/?hl=es',
        logo: '/images/press/corazon-del-negocio.png',
        badgeLogo: INSTAGRAM_PLATFORM_BADGE,
    },
    {
        key: 'instagramTurnoEnVivo' as const,
        label: 'Instagram · @turnoenvivo',
        href: 'https://www.instagram.com/p/DW4bn19Dwe6/?hl=es&img_index=1',
        logo: '/images/press/turno-en-vivo.png',
        badgeLogo: INSTAGRAM_PLATFORM_BADGE,
    },
    {
        key: 'instagramSomosYuly' as const,
        label: 'Instagram · @somos.yuly',
        href: 'https://www.instagram.com/p/DTTjltclLGk/?hl=es&img_index=1',
        logo: '/images/press/somos-yuly.png',
        badgeLogo: INSTAGRAM_PLATFORM_BADGE,
    },
    {
        key: 'instagramFranciscoAckermann' as const,
        label: 'Instagram · @francisco.ackermann',
        href: 'https://www.instagram.com/p/DTbjb4AjGCt/?hl=es&img_index=2',
        logo: '/images/press/francisco-ackermann.png',
        badgeLogo: INSTAGRAM_PLATFORM_BADGE,
    },
    {
        key: 'instagramLaJuntaPlus' as const,
        label: 'Instagram · @lajuntaplus',
        href: 'https://www.instagram.com/p/DTgByxqDuSL/?hl=es&img_index=1',
        logo: '/images/press/la-junta-plus.png',
        badgeLogo: INSTAGRAM_PLATFORM_BADGE,
    },
] as const;

export type PressQuoteBlock = {
    text: string;
    /** When false, renders text as-is (e.g. partial quote + attribution) */
    wrapQuotes?: boolean;
};

export type PressQuote = {
    id: string;
    linkKey: PressLinkKey;
    attribution: string;
    /** ISO date (YYYY-MM-DD) for carousel ordering — newest first */
    publishedAt: string;
    quotes: readonly PressQuoteBlock[];
    /** CTA label; defaults to "Ver noticia" */
    ctaLabel?: string;
    /** Override article/post URL (e.g. per-post Instagram links) */
    href?: string;
    /** Override outlet/account logo */
    logo?: string;
    /** Show ranking-style stacked thumb: account image + Instagram platform badge */
    showInstagramBadge?: boolean;
};

const PRESS_QUOTES_UNSORTED: PressQuote[] = [
    {
        id: 'instagram-daniel-mt',
        linkKey: 'instagram',
        attribution: 'Instagram · @daniel_m_t, Lo Que Importa Chile',
        publishedAt: '2026-08-10',
        ctaLabel: 'Ver en Instagram',
        showInstagramBadge: true,
        quotes: [
            {
                text: 'Hay reconocimientos que uno ni se imagina que puedan llegar. Por ejemplo, que un podcast que armamos porque queríamos conversar se convierta en #1 en este ranking de programas de YouTube en Chile. Sin gritos, sin polémicas artificiales, sin clickbait. Solo conversando.',
            },
        ],
    },
    {
        id: 'instagram-somos-fabuloso',
        linkKey: 'instagram',
        href: 'https://www.instagram.com/p/DagU8UmD5hx/?hl=es&img_index=6',
        logo: '/images/press/somos-fabuloso.jpg',
        attribution: 'Instagram · @somosfabuloso, Fabuloso',
        publishedAt: '2026-07-15',
        ctaLabel: 'Ver en Instagram',
        showInstagramBadge: true,
        quotes: [
            {
                text: 'Hoy celebramos a Alison Mandel en #MartesDePololeo 🥳🎂 Y otra vez siendo NÚMERO UNO 1️⃣ Capítulo completo en nuestro canal de YouTube. #SomosFabuloso',
            },
        ],
    },
    {
        id: 'instagram-corazon-del-negocio',
        linkKey: 'instagram',
        href: 'https://www.instagram.com/p/DaV9LweAKiA/?hl=es',
        logo: '/images/press/corazon-del-negocio.png',
        attribution: 'Instagram · @corazondelnegocio, Corazón del Negocio',
        publishedAt: '2026-06-22',
        ctaLabel: 'Ver en Instagram',
        showInstagramBadge: true,
        quotes: [
            {
                text: 'Esta semana el Radar Podscope publicó el ranking de Programas x Marcas (15-21 de junio) y nos alegra contarles que Corazón del Negocio entró al Top 10, en el puesto #9, con 6 marcas. Un lindo termómetro de cómo las marcas siguen apostando por el podcasting como espacio de conexión real.',
            },
        ],
    },
    {
        id: 'instagram-turno-en-vivo',
        linkKey: 'instagram',
        href: 'https://www.instagram.com/p/DW4bn19Dwe6/?hl=es&img_index=1',
        logo: '/images/press/turno-en-vivo.png',
        attribution: 'Instagram · @turnoenvivo, Turno en Vivo',
        publishedAt: '2026-04-09',
        ctaLabel: 'Ver en Instagram',
        showInstagramBadge: true,
        quotes: [
            {
                text: '¡Turno en Vivo gana la mañana del miércoles 8 en el stream! De acuerdo a @podscope.co, plataforma especializada en el seguimiento y análisis de contenidos digitales en vivo, Turno en Vivo lideró el bloque entre las 10:00 y las 12:00 con conversaciones de primer nivel e invitados estelares.',
            },
        ],
    },
    {
        id: 'instagram-somos-yuly',
        linkKey: 'instagram',
        href: 'https://www.instagram.com/p/DTTjltclLGk/?hl=es&img_index=1',
        logo: '/images/press/somos-yuly.png',
        attribution: 'Instagram · @somos.yuly, Somos Yuly',
        publishedAt: '2026-01-15',
        ctaLabel: 'Ver en Instagram',
        showInstagramBadge: true,
        quotes: [
            {
                text: 'Más de 20 millones de views después, seguimos conversando como si estuviéramos en la mesa del living. Top 5 de canales de podcast más vistos en Chile. +20M views en YouTube y Spotify durante 2025. Sí, vendimos un poco de humo. Pero los números no mienten.',
            },
        ],
    },
    {
        id: 'instagram-francisco-ackermann',
        linkKey: 'instagram',
        href: 'https://www.instagram.com/p/DTbjb4AjGCt/?hl=es&img_index=2',
        logo: '/images/press/francisco-ackermann.png',
        attribution: 'Instagram · @francisco.ackermann, Con Peras y Finanzas',
        publishedAt: '2026-01-20',
        ctaLabel: 'Ver en Instagram',
        showInstagramBadge: true,
        quotes: [
            {
                text: 'Qué emocionante, TOP 10 en YouTube!!! Tremendo estudio se mandó @podscope.co. Fuimos el único canal dentro de los 10 más vistos del país que no tiene relación con ocio y entretenimiento, sino con educación financiera.',
            },
        ],
    },
    {
        id: 'instagram-la-junta-plus',
        linkKey: 'instagram',
        href: 'https://www.instagram.com/p/DTgByxqDuSL/?hl=es&img_index=1',
        logo: '/images/press/la-junta-plus.png',
        attribution: 'Instagram · @lajuntaplus, La Junta Plus',
        publishedAt: '2026-01-22',
        ctaLabel: 'Ver en Instagram',
        showInstagramBadge: true,
        quotes: [
            {
                text: '¡La Junta lo hizo otra vez! Con cada año que avanza el programa confirma su consagración como uno de los más importantes de YouTube Chile y este estudio de @podscope.co lo demuestra. ¡Vamos por otro año más de muchos éxitos!',
            },
        ],
    },
    {
        id: 'the-clinic',
        linkKey: 'theClinic',
        attribution: 'The Clinic · Tiempo Libre, 9 de abril de 2026',
        publishedAt: '2026-04-09',
        quotes: [
            {
                text: 'Además, según la medición de la consultora Radar Podscope, el pódcast se ubicó en el primer lugar en contenido divulgado en redes sociales entre los más reproducidos durante la última semana de abril.',
            },
        ],
    },
    {
        id: 'radio-agricultura',
        linkKey: 'radioAgricultura',
        attribution: 'Radio Agricultura · Entretención, 9 de abril de 2026',
        publishedAt: '2026-04-09',
        quotes: [
            {
                text: 'Además, según mediciones de la consultora Radar Podscope, el pódcast se posicionó en el primer lugar entre los contenidos más reproducidos en redes durante la última semana de abril.',
            },
        ],
    },
    {
        id: 'entre-nosotras',
        linkKey: 'entreNosotras',
        attribution: 'AS Chile · Entre Nosotras, mayo 2026',
        publishedAt: '2026-05-01',
        quotes: [
            {
                text: 'El espacio digital ya comienza a posicionarse en los rankings de podcast —según la fuente Radar Podscope— alcanzando el cuarto lugar del Top Ten chileno, un logro significativo considerando su reciente estreno.',
            },
        ],
    },
    {
        id: 'diario-financiero',
        linkKey: 'diarioFinanciero',
        attribution: 'Diario Financiero · Sobre Fabuloso, febrero 2026',
        publishedAt: '2026-02-01',
        quotes: [
            {
                text: 'Como se trata de un nuevo medio, cuesta estandarizar la medición de audiencia, uno de los referentes que tienen es el ranking semanal que genera @podscope.co',
            },
        ],
    },
    {
        id: 'audiogen',
        linkKey: 'audioGen',
        attribution: 'AudioGen.fm · 3x3 #116, octubre 2025',
        publishedAt: '2025-10-01',
        quotes: [
            {
                text: 'Podscope rastreó las inserciones publicitarias en 60 podcasts en YouTube: 488 menciones a marcas de 83 anunciantes en 300 episodios. El informe concluye que estar más no significa ser más visto, lo estratégico es entender en qué podcast se coloca la publicidad..',
            },
        ],
    },
];

/** Press quotes for the landing carousel, newest first */
export const PRESS_QUOTES = [...PRESS_QUOTES_UNSORTED].sort((a, b) =>
    b.publishedAt.localeCompare(a.publishedAt)
);
